import { legacyRequest } from './client';

/** The fair's sign-in setup and status, from `/public/fairbydomain`. */
export interface LegacyLoginConfig {
  /** Empty when the fair has no Facebook app; the button is then hidden. */
  fbAppId: string | null;
  googleClientId: string | null;
  /** The legacy app only offered chat while the fair was running. */
  active: boolean;
}

export interface ChatMessage {
  id: number;
  createdAt: string;
  content: string | null;
  /** Set on the exhibitor's replies, null on the visitor's own messages. */
  stand: unknown;
}

/** Sent by the exhibitor in place of text to invite the visitor to a video call. */
export const VIDEO_INVITE = '{CHAT_INVITE}';

export type SignInProvider = 'google' | 'facebook';

export async function fetchLoginConfig(domain: string): Promise<LegacyLoginConfig> {
  const fair = await legacyRequest<Partial<LegacyLoginConfig>>('/public/fairbydomain', {
    query: { domain },
  });

  return {
    fbAppId: fair.fbAppId ?? null,
    googleClientId: fair.googleClientId ?? null,
    active: fair.active === true,
  };
}

/**
 * Exchanges a provider access token for the legacy API's JWT, creating the
 * `user` row on first sign-in. The token travels in the query string because
 * that is the endpoint's contract.
 */
export async function signIn(
  provider: SignInProvider,
  accessToken: string,
  domain: string,
): Promise<string> {
  const { token } = await legacyRequest<{ token: string }>(`/auth/${provider}`, {
    query: { token: accessToken, domain },
  });

  return token;
}

/** Messages after `lastMessageId`, oldest first. Marks the chat read by the visitor. */
export function fetchMessages(
  token: string,
  standId: number,
  lastMessageId: number | undefined,
): Promise<ChatMessage[]> {
  return legacyRequest<ChatMessage[]>('/chat', {
    token,
    query: { stand: standId, last_message_id: lastMessageId },
  });
}

/** Creates the chat on the first message. */
export function sendMessage(token: string, standId: number, content: string): Promise<ChatMessage> {
  return legacyRequest<ChatMessage>('/chat/send', {
    method: 'POST',
    token,
    form: { stand: standId, content },
  });
}

/** The Jitsi room for a video call with the stand. */
export async function fetchVideoRoom(token: string, standId: number): Promise<string | undefined> {
  const { hash } = await legacyRequest<{ hash?: string | null }>('/chat/info', {
    token,
    query: { stand: standId },
  });

  return hash ?? undefined;
}

export async function fetchNotificationsEnabled(token: string): Promise<boolean> {
  const { chatNotificationsEnabled } = await legacyRequest<{
    chatNotificationsEnabled: boolean | number;
  }>('/chat/notification', { token });

  return Boolean(chatNotificationsEnabled);
}

/** The endpoint flips the setting; it takes no value. */
export async function toggleNotifications(token: string): Promise<boolean> {
  const { chatNotificationsEnabled } = await legacyRequest<{
    chatNotificationsEnabled: boolean | number;
  }>('/chat/notification', { method: 'POST', token });

  return Boolean(chatNotificationsEnabled);
}
